import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ContentChildren,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  DYNAMIC_FORM_CONTROL_TYPE_CHECKBOX,
  DYNAMIC_FORM_CONTROL_TYPE_CHECKBOX_GROUP,
  DYNAMIC_FORM_CONTROL_TYPE_DATEPICKER,
  DYNAMIC_FORM_CONTROL_TYPE_GROUP,
  DYNAMIC_FORM_CONTROL_TYPE_INPUT,
  DYNAMIC_FORM_CONTROL_TYPE_RADIO_GROUP,
  DYNAMIC_FORM_CONTROL_TYPE_SELECT,
  DYNAMIC_FORM_CONTROL_TYPE_SLIDER,
  DYNAMIC_FORM_CONTROL_TYPE_SWITCH,
  DYNAMIC_FORM_CONTROL_TYPE_TEXTAREA,
  DynamicFormArrayGroupModel,
  DynamicFormComponentService,
  DynamicFormControl,
  DynamicFormControlContainerComponent,
  DynamicFormControlEvent,
  DynamicFormControlModel,
  DynamicFormLayout,
  DynamicFormLayoutService,
  DynamicFormRelationService,
  DynamicFormValidationService,
  DynamicFormValueControlModel,
  DynamicInputModel,
  DynamicTemplateDirective,
} from '@ng-dynamic-forms/core';
import { CmsService } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { TooltipIconService } from 'src/app/service/tooltip-icon/tooltip-icon.service';
import { DynamicMaterialCheckboxComponent } from './checkbox/dynamic-material-checkbox.component';
import { DynamicMaterialChipsComponent } from './chips/dynamic-material-chips.component';
import { DynamicMaterialDatePickerComponent } from './datepicker/dynamic-material-datepicker.component';
// import { DynamicMaterialFormArrayComponent } from "./form-array/dynamic-material-form-array.component";
import { DynamicMaterialFormGroupComponent } from './form-group/dynamic-material-form-group.component';
import { DynamicMaterialInputComponent } from './input/dynamic-material-input.component';
import { DynamicMaterialRadioGroupComponent } from './radio-group/dynamic-material-radio-group.component';
import { DynamicMaterialSelectComponent } from './select/dynamic-material-select.component';
import { DynamicMaterialSlideToggleComponent } from './slide-toggle/dynamic-material-slide-toggle.component';
import { DynamicMaterialSliderComponent } from './slider/dynamic-material-slider.component';
import { DynamicMaterialTextAreaComponent } from './textarea/dynamic-material-textarea.component';

@Component({
  selector: 'dynamic-material-form-control',
  templateUrl: './dynamic-material-form-control-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DynamicMaterialFormControlContainerComponent
  extends DynamicFormControlContainerComponent
  implements OnInit, OnDestroy
{
  @ContentChildren(DynamicTemplateDirective) contentTemplateList: QueryList<DynamicTemplateDirective>;

  @HostBinding('class') klass: any;

  @Input() context: DynamicFormArrayGroupModel | null = null;
  @Input() group: FormGroup;
  @Input() hostClass: string[];
  @Input('templates') inputTemplateList: QueryList<DynamicTemplateDirective>;
  @Input() layout: DynamicFormLayout;
  @Input() getLanguage: any;
  @Input() model: any;
  /* commented this temporarily
    // @Input() model: DynamicFormControlModel;
  */

  @Output() blur: EventEmitter<DynamicFormControlEvent> = new EventEmitter<DynamicFormControlEvent>();
  @Output() change: EventEmitter<DynamicFormControlEvent> = new EventEmitter<DynamicFormControlEvent>();
  @Output() focus: EventEmitter<DynamicFormControlEvent> = new EventEmitter<DynamicFormControlEvent>();
  @Output('matEvent') customEvent: EventEmitter<DynamicFormControlEvent> = new EventEmitter<DynamicFormControlEvent>();

  @ViewChild('componentViewContainer', { read: ViewContainerRef, static: true })
  componentViewContainerRef: ViewContainerRef;
  currentPageSubscription$: Subscription;
  controlTooltipIconConfig$: Observable<any>;

  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    protected componentFactoryResolver: ComponentFactoryResolver,
    protected layoutService: DynamicFormLayoutService,
    protected validationService: DynamicFormValidationService,
    protected componentService: DynamicFormComponentService,
    protected relationService: DynamicFormRelationService,
    private cmsService: CmsService,
    private cdr: ChangeDetectorRef,
    private countryService: ShimanoCountryService,
    private tooltipIconService: TooltipIconService
  ) {
    super(
      changeDetectorRef,
      componentFactoryResolver,
      layoutService,
      validationService,
      componentService,
      relationService
    );
  }
  ngOnInit(): void {
    this.currentPageSubscription$ = this.cmsService.getCurrentPage().subscribe((currentPageDetails) => {
      if (currentPageDetails?.template === 'SavedCartDetailsPageTemplate') {
        this.model.additional = { showLabelOnInit: true };
      }
      this.cdr.detectChanges();
    });

    this.controlTooltipIconConfig$ = this.countryService.getActive().pipe(
      take(1),
      switchMap((activeCountry) => this.tooltipIconService.getTooltipIconConfig(activeCountry, this.model.id))
    );

    this.cdr.detectChanges();
  }

  get componentType(): Type<DynamicFormControl> | null {
    return this.componentService.getCustomComponentType(this.model) || materialUIFormControlMapFn(this.model);
  }

  get hasMatFormField(): boolean {
    const matFormFieldTypes = [
      DYNAMIC_FORM_CONTROL_TYPE_DATEPICKER,
      DYNAMIC_FORM_CONTROL_TYPE_INPUT,
      DYNAMIC_FORM_CONTROL_TYPE_SELECT,
      DYNAMIC_FORM_CONTROL_TYPE_TEXTAREA,
    ];

    return (
      matFormFieldTypes.some((type) => this.model.type === type) ||
      (this.model instanceof DynamicFormValueControlModel && this.model.getAdditional('isFormFieldControl'))
    );
  }
  ngOnDestroy() {
    if (this.currentPageSubscription$) {
      this.currentPageSubscription$.unsubscribe();
    }
  }
}

export function materialUIFormControlMapFn(model: DynamicFormControlModel): Type<DynamicFormControl> | null {
  switch (model.type) {
    case DYNAMIC_FORM_CONTROL_TYPE_CHECKBOX:
      return DynamicMaterialCheckboxComponent;

    case DYNAMIC_FORM_CONTROL_TYPE_CHECKBOX_GROUP:
      return DynamicMaterialFormGroupComponent;

    case DYNAMIC_FORM_CONTROL_TYPE_DATEPICKER:
      return DynamicMaterialDatePickerComponent;

    case DYNAMIC_FORM_CONTROL_TYPE_GROUP:
      return DynamicMaterialFormGroupComponent;

    case DYNAMIC_FORM_CONTROL_TYPE_INPUT:
      const inputModel = model as DynamicInputModel;

      return inputModel.multiple ? DynamicMaterialChipsComponent : DynamicMaterialInputComponent;

    case DYNAMIC_FORM_CONTROL_TYPE_RADIO_GROUP:
      return DynamicMaterialRadioGroupComponent;

    case DYNAMIC_FORM_CONTROL_TYPE_SELECT:
      return DynamicMaterialSelectComponent;

    case DYNAMIC_FORM_CONTROL_TYPE_SLIDER:
      return DynamicMaterialSliderComponent;

    case DYNAMIC_FORM_CONTROL_TYPE_SWITCH:
      return DynamicMaterialSlideToggleComponent;

    case DYNAMIC_FORM_CONTROL_TYPE_TEXTAREA:
      return DynamicMaterialTextAreaComponent;

    default:
      return null;
  }
}
